import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { ChevronRight, CheckCircle, ArrowLeft, ArrowRight, BookOpen, Clock, Activity, FileText, Brain, Play, Check, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import SimulatorTask from '../components/learn/SimulatorTask';
import Flashcard from '../components/learn/Flashcard';
import { AtomStructure, QuarkTable, StandardModelDiagram, ForceCarriers } from '../components/learn/infographics/ParticleInfographics';
import modulesData from '../data/modules.json';
import { showToast } from '../components/common/Toast';
import { useLocalStorage } from '../hooks/useLocalStorage';
import confetti from 'canvas-confetti';
import { gsap, ScrollTrigger } from '../hooks/useGSAP';

// Use layout effect with SSR safety
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
// Infographic component mapping
const INFOGRAPHICS = {
   'AtomStructure': AtomStructure,
   'QuarkTable': QuarkTable,
   'StandardModelDiagram': StandardModelDiagram,
   'ForceCarriers': ForceCarriers,
};

const SimpleQuiz = ({ questionData }) => {
   const [selected, setSelected] = useState(null);
   const [isCorrect, setIsCorrect] = useState(null);

   const handleSelect = (idx) => {
      if (isCorrect === true) return;
      setSelected(idx);
      const correct = idx === questionData.correct;
      setIsCorrect(correct);
      if (correct) {
         confetti({ particleCount: 50, spread: 40, origin: { y: 0.8 }, startVelocity: 20 });
      }
   };

   return (
      <div className="bg-white border rounded-xl p-6 shadow-sm my-6">
         <div className="flex items-center gap-2 mb-4">
            <CheckCircle size={18} className="text-black" />
            <h3 className="font-bold text-slate-800">Quick Check</h3>
         </div>
         <p className="text-lg font-medium text-slate-800 mb-4">{questionData.q}</p>
         <div className="space-y-2">
            {questionData.a.map((option, idx) => (
               <button
                  key={idx}
                  onClick={() => handleSelect(idx)}
                  className={`w-full text-left p-3 rounded-lg border transition-all flex justify-between items-center
                            ${selected === idx
                        ? (isCorrect ? 'bg-black text-white border-black' : 'bg-gray-100 border-gray-300 text-black')
                        : 'bg-white border-gray-200 hover:bg-gray-50'}
                        `}
               >
                  <span>{option}</span>
                  {selected === idx && (
                     <span>{isCorrect ? <CheckCircle size={18} /> : <X size={18} />}</span>
                  )}
               </button>
            ))}
         </div>
         {selected !== null && (
            <div className="mt-4">
               {isCorrect && <p className="text-sm text-black font-semibold">Correct! Well done.</p>}
               {questionData.explanation && (
                  <p className="mt-2 text-sm text-gray-700 bg-gray-50 p-3 rounded-lg border border-gray-200">
                     {questionData.explanation}
                  </p>
               )}
            </div>
         )}
      </div>
   );
};

const Learn = () => {
   const [activeLevel, setActiveLevel] = useState('beginner');
   const [selectedModuleId, setSelectedModuleId] = useState('1.1.1');
   const [currentModuleIndex, setCurrentModuleIndex] = useState(0);

   // GSAP refs
   const sidebarRef = useRef(null);
   const contentRef = useRef(null);
   const headerRef = useRef(null);

   const levels = { beginner: 0, intermediate: 1, advanced: 2 };
   const currentLevelItems = modulesData.modules.find(l => l.level === activeLevel)?.items || [];
   const currentModule = currentLevelItems[currentModuleIndex] || currentLevelItems[0];

   // GSAP animations for sidebar modules
   useIsomorphicLayoutEffect(() => {
      if (!sidebarRef.current) return;

      const ctx = gsap.context(() => {
         const modules = sidebarRef.current.querySelectorAll('.module-item');

         gsap.fromTo(modules,
            { opacity: 0, x: -30 },
            {
               opacity: 1,
               x: 0,
               duration: 0.5,
               stagger: 0.05,
               ease: 'power4.out',
            }
         );
      }, sidebarRef);

      return () => ctx.revert();
   }, [activeLevel]);

   // GSAP animations for content sections
   useIsomorphicLayoutEffect(() => {
      if (!contentRef.current) return;

      const ctx = gsap.context(() => {
         // Animate header
         if (headerRef.current) {
            gsap.fromTo(headerRef.current.children,
               { opacity: 0, y: 30 },
               { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: 'power4.out' }
            );
         }

         // Animate topic sections
         const topics = contentRef.current.querySelectorAll('.topic-section');
         gsap.fromTo(topics,
            { opacity: 0, y: 50 },
            {
               opacity: 1,
               y: 0,
               duration: 0.7,
               stagger: 0.15,
               ease: 'power4.out',
               scrollTrigger: {
                  trigger: contentRef.current,
                  start: 'top 80%',
                  toggleActions: 'play none none none'
               }
            }
         );
      }, contentRef);

      return () => ctx.revert();
   }, [currentModuleIndex, activeLevel]);

   // Handlers
   const handleLevelChange = (level) => {
      setActiveLevel(level);
      setCurrentModuleIndex(0);
   };

   return (
      <div className="flex flex-col lg:flex-row min-h-screen bg-[#f5f5f7]">

         {/* Sidebar Navigation */}
         <div ref={sidebarRef} className="w-full lg:w-[320px] bg-white border-r border-gray-200 flex flex-col h-screen sticky top-0 z-30">

            {/* Level Tabs */}
            <div className="flex p-2 gap-1 bg-[#f5f5f7] border-b border-gray-200">
               {['beginner', 'intermediate', 'advanced'].map(level => (
                  <button
                     key={level}
                     onClick={() => handleLevelChange(level)}
                     className={`
                    flex-1 py-2 text-xs font-bold rounded-md capitalize transition-colors tracking-wide
                    ${activeLevel === level
                           ? 'bg-[#1d1d1f] text-white shadow-sm'
                           : 'text-gray-500 hover:bg-gray-100'}
                  `}
                  >
                     {level}
                  </button>
               ))}
            </div>

            {/* Module List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
               <div className="px-2 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Chapters
               </div>
               {currentLevelItems.map((module, idx) => (
                  <button
                     key={idx}
                     onClick={() => setCurrentModuleIndex(idx)}
                     className={`module-item
                    w-full text-left p-3 rounded-lg flex items-start gap-3 transition-colors group
                    ${currentModuleIndex === idx ? 'bg-[#1d1d1f]/5 border border-gray-200' : 'hover:bg-gray-50 border border-transparent'}
                  `}
                  >
                     <div className={`
                      flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-colors
                      ${currentModuleIndex === idx ? 'bg-[#1d1d1f] text-white' : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200'}
                   `}>
                        <span className="text-xs font-bold">{idx + 1}</span>
                     </div>
                     <div>
                        <h4 className={`text-sm font-semibold leading-tight ${currentModuleIndex === idx ? 'text-[#1d1d1f]' : 'text-gray-700'}`}>
                           {module.title}
                        </h4>
                        <p className="text-[10px] text-gray-400 mt-1 line-clamp-1">{module.description}</p>
                     </div>
                  </button>
               ))}
            </div>
         </div>

         {/* Main Content Area */}
         <div ref={contentRef} className="flex-1 bg-[#FAFAFA] overflow-y-auto pt-28">
            {currentModule ? (
               <div className="max-w-4xl mx-auto px-6 py-12">

                  <div ref={headerRef} className="mb-10">
                     <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium uppercase tracking-wider">
                        <span className="w-2 h-2 rounded-full bg-[#1d1d1f]"></span>
                        {activeLevel} Level
                        <ChevronRight size={12} />
                        Chapter {currentModuleIndex + 1}
                     </div>
                     <h1 className="text-4xl md:text-5xl font-bold text-[#1d1d1f] mb-4 tracking-tight">{currentModule.title}</h1>
                     <p className="text-xl text-[#86868b] leading-relaxed font-light">{currentModule.description}</p>
                  </div>

                  <div className="space-y-12">
                     {currentModule.subtopics && currentModule.subtopics.map((topic, index) => (
                        <div key={index} className="topic-section scroll-mt-24" id={`topic-${index}`}>

                           {/* Topic Title */}
                           <div className="flex items-center gap-3 mb-6">
                              <span className="text-lg font-bold text-gray-300">{(currentModuleIndex + 1)}.{index + 1}</span>
                              <h2 className="text-2xl font-bold text-[#1d1d1f]">{topic.title}</h2>
                           </div>

                           {/* Content based on Type */}
                           <div className="">
                              {topic.type === 'text' && (
                                 <div className="prose prose-lg prose-neutral max-w-none bg-white p-8 rounded-2xl border border-gray-100 shadow-sm prose-headings:text-[#1d1d1f] prose-p:text-[#424245] prose-strong:text-[#1d1d1f] prose-table:text-sm prose-th:bg-gray-50 prose-th:p-3 prose-td:p-3 prose-td:border-gray-200">
                                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>{topic.content}</ReactMarkdown>
                                    {/* Render infographic if specified */}
                                    {topic.infographic && INFOGRAPHICS[topic.infographic] && (
                                       React.createElement(INFOGRAPHICS[topic.infographic])
                                    )}
                                 </div>
                              )}

                              {topic.type === 'case_study' && (
                                 <div className="bg-white border border-gray-200 rounded-2xl p-8 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-4 opacity-10">
                                       <BookOpen size={120} className="text-black" />
                                    </div>
                                    <span className="relative z-10 px-3 py-1 bg-gray-100 text-gray-900 text-xs font-bold uppercase rounded-full mb-4 inline-block">Case Study</span>
                                    <div className="relative z-10 prose prose-lg prose-neutral max-w-none">
                                       <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>{topic.content}</ReactMarkdown>
                                    </div>
                                 </div>
                              )}

                              {topic.type === 'task' && (
                                 <SimulatorTask
                                    taskDescription={topic.content}
                                    targetPreset={null}
                                 />
                              )}

                              {topic.type === 'quiz' && topic.quiz && (
                                 <SimpleQuiz questionData={topic.quiz} />
                              )}

                              {topic.type === 'flashcard' && topic.flashcard && (
                                 <Flashcard front={topic.flashcard.front} back={topic.flashcard.back} />
                              )}
                           </div>
                        </div>
                     ))}
                  </div>

                  {/* Navigation Footer */}
                  <div className="flex items-center justify-between border-t border-slate-200 pt-10 mt-20">
                     <Button
                        variant="ghost"
                        icon={ArrowLeft}
                        onClick={() => setCurrentModuleIndex(Math.max(0, currentModuleIndex - 1))}
                        disabled={currentModuleIndex === 0}
                     >
                        Previous Chapter
                     </Button>
                     <Button
                        variant="primary"
                        icon={ArrowRight}
                        onClick={() => setCurrentModuleIndex(Math.min(currentLevelItems.length - 1, currentModuleIndex + 1))}
                        disabled={currentModuleIndex === currentLevelItems.length - 1}
                     >
                        Next Chapter
                     </Button>
                  </div>

               </div>
            ) : (
               <div className="flex items-center justify-center h-full text-slate-400">
                  Loading curriculum...
               </div>
            )}
         </div>

      </div >
   );
};

export default Learn;
